import { ChangeEvent, ComponentProps, FC, useEffect, useState } from 'react';

import { clientConfig } from '@/config/clientConfig';
import { useApi } from '@/shared/api';
import { useApiMyhub } from '@/shared/apiMyhub/useApiMyhub';
import { AccountStatus } from '@/shared/generated/graphql';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { BeneficiaryDto } from '@/shared/lib/sj-orm/models/beneficiary.dto';

import { DrawerPickArrayOfDto } from '../drawer-pick-array-of-dto';

import { BeneficiariesDrawer } from './components/BeneficiariesDrawer';
import { NextOwnerUser } from './components/type';

type DrawerPickBeneficiaryProps = Omit<
  ComponentProps<typeof DrawerPickArrayOfDto>,
  'collection'
> & {
  label?: string;
  isSelectPersonMode?: boolean;
};

export const DrawerPickBeneficiary: FC<DrawerPickBeneficiaryProps> = ({
  control,
  isOpen,
  memoValue,
  name,
  type,
  label,
  isSelectPersonMode,
  filter,
  setIsOpen,
  setValue,
  onFieldChange,
  mode,
}) => {
  const router = useAppRouter();
  const sjDb = useSJDatabase();
  const api = useApi();
  const apiMyHub = useApiMyhub();

  const [searchValue, setSearchValue] = useState('');
  const [userList, setUserList] = useState<Array<NextOwnerUser>>([]);
  const [isNeedRefetch, setIsNeedRefetch] = useState(false);
  const [invitedUsers, setInvitedUsers] = useState<Array<string>>([]);

  const refetchNestOwners = () => {
    setIsNeedRefetch((flag) => !flag);
  };

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const dtos =
    sjDb.collections.beneficiaries
      ?.findMany(
        (item: BeneficiaryDto) =>
          Boolean(item.firstName || item.nickName || item.email) &&
          (filter?.(item as BeneficiaryDto, memoValue.current, name) ?? true),
      )
      .map((item: BeneficiaryDto) => {
        return {
          createdAt: item.createdAt,
          email: item.email,
          group: item.group,
          label: item.firstName || item.nickName || item.email,
          value: item.id,
          centralDbProfileId: item.centralDbProfileId,
          invitationCode: item.invitationCode,
        };
      }) || [];

  const handleSelect = (value: string[]) => {
    if (mode === 'multiple') {
      setSelectedItems(value);
      memoValue.current[name] = value;

      onFieldChange?.(value);
    } else {
      setSelectedItems(value);
      memoValue.current[name] = value[0];

      onFieldChange?.(value[0]);
    }
  };

  useEffect(() => {
    if (Array.isArray(memoValue.current[name])) {
      setSelectedItems(memoValue.current[name]);
    }
  }, [memoValue.current[name]]);

  useEffect(() => {
    const fetchUserData = async () => {
      const res = clientConfig.isExported
        ? await apiMyHub.getProfileBeneficiaries()
        : await api.profilesGetBeneficiaries();

      const updatedUsers = dtos.map((user) => {
        const currentUser = res.data?.find(
          (beneficiary) => beneficiary.id === user.centralDbProfileId,
        );
        if (!currentUser) return user;

        return {
          ...currentUser,
          ...user,
          id: String(currentUser?.id),
          invitationLastSentTime: currentUser.invitationLastSentTime
            ? new Date(currentUser.invitationLastSentTime)
            : null,
        };
      });

      setUserList(updatedUsers);
    };

    fetchUserData();
  }, [dtos.length, isNeedRefetch]);

  useEffect(() => {
    if (memoValue.current[name] && !control._formValues[name]) {
      setValue(name, memoValue.current[name]);
    }
  }, [memoValue.current, name]);

  const inputHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const lowerCase = e.target.value.toLowerCase().trim();
    setSearchValue(lowerCase);
  };

  const filteredUserList =
    searchValue === ''
      ? userList
      : userList
          .filter((el) => {
            const userLabel = el.label;
            const userEmail = el.email;

            if (!userLabel && !userEmail) {
              return false;
            }

            return !!(
              userLabel?.toLowerCase().includes(searchValue) ||
              userEmail?.toLowerCase().includes(searchValue)
            );
          })
          .sort((a, b) => {
            if (a.label && b.label) {
              return a.label.localeCompare(b.label);
            } else if (a.email && b.email) {
              return a.email.localeCompare(b.email);
            }

            return 0;
          });

  const onAddNewClick = async () => {
    if (!type) {
      return;
    }

    localStorage.removeItem(`new-family-member-added`);

    await router.replace({
      pathname: window.location.pathname,
      query: {
        fastAddCollection: CollectionName.BENEFICIARIES,
        fastAddTypes: type,
        fastTrack: true,
      },
    });
  };

  const onSelectClick = () => {
    setIsOpen(false);
    localStorage.removeItem(`new-family-member-added`);
  };

  const registeredUser = filteredUserList
    .filter((user) => user.status === AccountStatus.Active)
    .sort((a, b) => a.label!.localeCompare(b.label!));

  const notRegisteredUser = filteredUserList
    .filter((user) => user.status === AccountStatus.Inactive && user.invitationLastSentTime)
    .sort((a, b) => a.label!.localeCompare(b.label!));

  const notInvitedUser = filteredUserList
    .filter((user) => user.status === AccountStatus.Inactive && !user.invitationLastSentTime)
    .sort((a, b) => a.label!.localeCompare(b.label!));

  const drawerLabel = label
    ? label
    : onFieldChange
    ? 'Assign a Family Member'
    : 'Select Family Member';

  return (
    <BeneficiariesDrawer
      isModalOpen={isOpen}
      filteredUser={filteredUserList}
      invitedUsers={invitedUsers}
      label={drawerLabel} // TODO
      notInvitedUser={notInvitedUser}
      notRegisteredUser={notRegisteredUser}
      registeredUser={registeredUser}
      searchValue={searchValue}
      selectedItems={selectedItems}
      isSelectPersonMode={isSelectPersonMode}
      handleSelect={handleSelect}
      onAddNewClick={onAddNewClick}
      onSelectClick={onSelectClick}
      refetchNestOwners={refetchNestOwners}
      searchInputHandler={inputHandler}
      setInvitedUsers={setInvitedUsers}
      setIsModalOpen={setIsOpen}
      mode={mode}
    />
  );
};
